// ./src/components/features/Connectors/ConfigEditors/GitHubConnectorConfigEditor.tsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Stack,
  TextInput,
  Text,
  Anchor,
  Alert,
  Code,
  Switch,
  Group,
  Box,
  List,
} from "@mantine/core";
import { IconInfoCircle, IconBrandGithub } from "@tabler/icons-react";
import { stringifyObject, parseObject } from "@/utils";
import { GitHubConnectorConfig } from "@/types";

export interface GitHubConnectorConfigEditorProps {
  /** JSON string of connector configuration */
  value: string;

  /** Callback when configuration changes */
  onChange: (value: string) => void;

  /** Whether the editor is disabled */
  disabled?: boolean;
}

// Default empty configuration
const DEFAULT_CONFIG: GitHubConnectorConfig = {
  accessToken: "",
  owner: "",
  repo: "",
  titlePattern: "{{formName}}: New submission",
  labels: "",
  includeMetadata: true,
};

/**
 * Parse and validate GitHub connector configuration
 */
const parseGitHubConfig = (jsonString: string): GitHubConnectorConfig => {
  const parsed = parseObject(jsonString, DEFAULT_CONFIG);
  return parsed;
};

export const GitHubConnectorConfigEditor: React.FC<
  GitHubConnectorConfigEditorProps
> = ({ value, onChange, disabled = false }) => {
  // Parse incoming value to config state
  const [config, setConfig] = useState<GitHubConnectorConfig>(() =>
    parseGitHubConfig(value)
  );

  // Sync config when external value changes
  useEffect(() => {
    const parsed = parseGitHubConfig(value);
    setConfig(parsed);
  }, [value]);

  // Update parent when config changes
  const updateConfig = useCallback(
    (newConfig: GitHubConnectorConfig) => {
      setConfig(newConfig);
      onChange(stringifyObject(newConfig, DEFAULT_CONFIG));
    },
    [onChange]
  );

  // Handle field changes
  const handleAccessTokenChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, accessToken: e.target.value });
  };

  const handleOwnerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, owner: e.target.value });
  };

  const handleRepoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, repo: e.target.value });
  };

  const handleTitlePatternChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, titlePattern: e.target.value });
  };

  const handleLabelsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, labels: e.target.value });
  };

  const handleIncludeMetadataChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, includeMetadata: e.currentTarget.checked });
  };

  return (
    <Stack gap="md">
      {/* Setup Instructions */}
      <Alert icon={<IconBrandGithub size={16} />} color="gray" variant="light">
        <Text size="sm" fw={500} mb="xs">
          Setup Instructions:
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>
            Go to{" "}
            <Anchor
              href="https://github.com/settings/tokens"
              target="_blank"
              rel="noopener noreferrer"
            >
              GitHub Settings → Tokens
            </Anchor>
          </List.Item>
          <List.Item>
            Click <strong>Generate new token</strong> (classic or fine-grained)
          </List.Item>
          <List.Item>
            For classic: select <Code>repo</Code> scope (or just{" "}
            <Code>public_repo</Code> for public repos)
          </List.Item>
          <List.Item>
            For fine-grained: grant <strong>Issues: Read and write</strong>{" "}
            permission
          </List.Item>
          <List.Item>Generate and copy the token</List.Item>
        </List>
      </Alert>

      {/* Access Token Field */}
      <TextInput
        label="Personal Access Token"
        placeholder="ghp_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
        description="Classic token (ghp_) or fine-grained token (github_pat_)"
        value={config.accessToken}
        onChange={handleAccessTokenChange}
        disabled={disabled}
        required
        size="md"
        type="password"
        autoComplete="off"
      />

      {/* Owner Field */}
      <TextInput
        label="Repository Owner"
        placeholder="username or organization"
        description="GitHub username or organization name"
        value={config.owner}
        onChange={handleOwnerChange}
        disabled={disabled}
        required
        size="md"
      />

      {/* Repo Field */}
      <TextInput
        label="Repository Name"
        placeholder="my-repo"
        description="Name of the repository (not the full URL)"
        value={config.repo}
        onChange={handleRepoChange}
        disabled={disabled}
        required
        size="md"
      />

      {/* Help for repo URL */}
      {config.owner && config.repo && (
        <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
          <Text size="sm">
            Issues will be created at:{" "}
            <Anchor
              href={`https://github.com/${config.owner}/${config.repo}/issues`}
              target="_blank"
              rel="noopener noreferrer"
            >
              github.com/{config.owner}/{config.repo}/issues
            </Anchor>
          </Text>
        </Alert>
      )}

      {/* Title Pattern Field */}
      <TextInput
        label="Issue Title Pattern"
        placeholder="{{formName}}: New submission"
        description="Customize the issue title"
        value={config.titlePattern || "{{formName}}: New submission"}
        onChange={handleTitlePatternChange}
        disabled={disabled}
        size="md"
      />

      {/* Labels Field */}
      <TextInput
        label="Labels"
        placeholder="form-submission, needs-review"
        description="Comma-separated labels to add (must already exist in repo)"
        value={config.labels || ""}
        onChange={handleLabelsChange}
        disabled={disabled}
        size="md"
      />

      {/* Toggle Options */}
      <Box>
        <Group justify="space-between">
          <div>
            <Text size="sm">Include Metadata</Text>
            <Text size="xs" c="dimmed">
              Add timestamp, submission ID, and view link to issue body
            </Text>
          </div>
          <Switch
            checked={config.includeMetadata !== false}
            onChange={handleIncludeMetadataChange}
            disabled={disabled}
          />
        </Group>
      </Box>

      {/* Placeholders Help */}
      <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
        <Text size="sm" fw={500} mb={4}>
          Available placeholders:
        </Text>
        <Text size="xs" component="div">
          <Code>{"{{formName}}"}</Code> — Form name
          <br />
          <Code>{"{{formId}}"}</Code> — Form UUID
          <br />
          <Code>{"{{requestId}}"}</Code> — Submission ID
          <br />
          <Code>{"{{date}}"}</Code> — Date (YYYY-MM-DD)
        </Text>
      </Alert>

      {/* How It Works */}
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        <Text size="sm" fw={500} mb="xs">
          How it works:
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>Each submission creates a new GitHub issue</List.Item>
          <List.Item>
            Issue body contains all form field data (Markdown formatted)
          </List.Item>
          <List.Item>
            Labels are added if specified and exist in the repository
          </List.Item>
          <List.Item>Hidden and security fields are excluded</List.Item>
        </List>
      </Alert>
    </Stack>
  );
};
